// A version 2 page's bindings, filled (cloud ADR 0393, TOM-665; VENDORED.md
// change 10; the contract is cloud/specs/board-pages-v2.md §4 and §7.2).
// drawPage indexes the bound elements once when it inserts a page, and fills
// them on every update: text through textContent, the host's state through
// data-zone, data-quality, data-state and data-tone, a history path's `d`, a
// fitted drawing's viewBox, and every fraction and point as a rule in the
// frame's style#tc-values (never an inline style: TOM-623's WebKit leak).
// Only what changed is written.
//
// The arithmetic is page-rules.js's, so the server's renderer and the web
// draw a page the same; this file reads the DOM and writes it.

import {
  PAGE_FRESHNESS_STATES,
  PAGE_HOST_ATTRIBUTES,
  pageAgeText,
  pageAlignedTicks,
  pageBindingValueAllowed,
  pageDayBands,
  pageSeriesKey,
  pageSeriesMark,
  pageSeriesPath,
  pageSeriesStat,
  pageTimeTicks,
  pageValueText,
  pageValueTicks,
  pageVariables,
  pageWindowBounds,
  pageWindowText,
  pageYRange,
  readingFraction,
  readingZone,
} from "./page-rules.js";
import { formatDuration } from "./units.js";

const numeric = (v) => typeof v === "number" && Number.isFinite(v);
const own = (record, key) =>
  record && Object.hasOwn(record, key) ? record[key] : undefined;
const nameOf = (el) => String(el.localName ?? el.nodeName ?? "").toLowerCase();

/**
 * What drawPage keeps of a version 2 page: its bound elements, fitted drawings
 * and live areas. A binding whose value is not one the vocabulary reads, or a
 * name it does not have, is left in the page as saved and bound to nothing
 * (TOM-1031); a chat page's `data-tc-result` binds only with
 * `{ results: true }` (cloud ADR 0399, TOM-870). A bound element loses any
 * PAGE_HOST_ATTRIBUTES the page wrote on it: the host sets those.
 */
export function pageBindingIndex(body, options = {}) {
  const bound = [],
    fits = [],
    live = [];
  const walk = (node) => {
    for (const child of node?.childNodes ?? []) {
      if (child.nodeType !== 1) continue;
      const tag = nameOf(child);
      if (tag === "tc-live") live.push(child);
      // The key is the host's own, never the page's: a key the page wrote
      // would take another element's values (TOM-665).
      child.removeAttribute?.("data-tc-key");
      const attrs = {};
      for (const { name, value } of child.attributes ?? []) {
        const key = String(name).toLowerCase();
        if (
          key.startsWith("data-tc-") &&
          pageBindingValueAllowed(key, value, options)
        )
          attrs[key] = String(value);
      }
      if (tag === "svg" && attrs["data-tc-fit"] !== undefined) fits.push(child);
      if (Object.keys(attrs).length > 0) {
        // On an element the host fills, its state attributes are the host's
        // alone (TOM-1031): a copy the page wrote would fake a reading's
        // zone, quality, state or tone. Anywhere else they are drawn as saved.
        for (const name of PAGE_HOST_ATTRIBUTES) child.removeAttribute?.(name);
        bound.push({ el: child, attrs, tag, key: bound.length });
      }
      walk(child);
    }
  };
  walk(body);
  return { bound, fits, live };
}

function windowOf(el) {
  for (let n = el; n && n.nodeType === 1; n = n.parentNode) {
    const w = n.getAttribute?.("data-tc-window");
    if (w !== null && w !== undefined) return { window: w, at: n };
  }
  return null;
}

function svgOf(el) {
  for (let n = el.parentNode; n && n.nodeType === 1; n = n.parentNode)
    if (nameOf(n) === "svg") return n;
  return null;
}

function fourNumbers(text) {
  if (typeof text !== "string") return null;
  const parts = text
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  return parts.length === 4 && parts.every(Number.isFinite) ? parts : null;
}

function selectorOf(attrs, keys) {
  return keys
    .filter((k) => attrs[k] !== undefined)
    .map((k) => `[${k}="${attrs[k].replace(/["\\]/g, "\\$&")}"]`)
    .join("");
}

function fixed(value, decimals) {
  const text = value.toFixed(Math.max(0, Math.min(20, decimals)));
  return /^-0(?:\.0+)?$/.test(text) ? text.slice(1) : text;
}

/**
 * The decimals a set of tick values needs, at most 6 (TOM-1001): the most
 * any one of them takes to be written exactly, so 98, 98.5, 99 read
 * "98.0, 98.5, 99.0" and 5, 10, 15 read "5, 10, 15".
 */
function pageTickDecimals(ticks) {
  let most = 0;
  for (const t of ticks) {
    if (!Number.isFinite(t)) continue;
    let k = 0;
    while (k < 6 && Math.abs(Math.round(t * 10 ** k) - t * 10 ** k) > 1e-6)
      k += 1;
    most = Math.max(most, k);
  }
  return most;
}

function decimalsOfText(text) {
  return (String(text ?? "").split(".")[1] ?? "").replace(/\D.*$/, "").length;
}

function readoutAge(reading, now) {
  if (!reading || !numeric(reading.measuredAt) || !numeric(now)) return "—";
  const s = Math.max(0, Math.round((now - reading.measuredAt) / 1000));
  const m = Math.round(s / 60);
  const h = Math.round(m / 60);
  const ago =
    s < 90
      ? `${s} s ago`
      : m < 90
        ? `${m} min ago`
        : h < 36
          ? `${h} h ago`
          : `${Math.round(h / 24)} d ago`;
  return reading.quality === "stale" ? `stale · ${ago}` : ago;
}

/**
 * The readings' values for one update (pageVariables): the rule the frame's
 * values style starts with (`--name` and `--name-fraction`) and the
 * attributes for the page's `<html>`, a null removing one. drawPage and a
 * scripted page's host (page-script.js, TOM-1038) both write these.
 */
export function pageRootValues(page, data) {
  const { properties, attributes } = pageVariables(page, data);
  const values = Object.entries(properties)
    .filter(([, value]) => value !== null)
    .map(([name, value]) => `${name}:${value}`)
    .join(";");
  return { rule: values ? `html:root:root{${values}}` : "", attributes };
}

/**
 * The texts of a page's `<tc-value>`, `<tc-unit>` and `<tc-age>` elements for
 * one update, as `[element, text]` pairs. drawPage and a scripted page's host
 * (page-script.js, TOM-1038) both write these.
 */
export function pageTagTexts(body, data) {
  const readings = data.readings ?? {};
  const reading = (element) => {
    const name = element.getAttribute("reading");
    return name && Object.hasOwn(readings, name) ? readings[name] : undefined;
  };
  const out = [];
  for (const element of body.querySelectorAll("tc-value"))
    out.push([
      element,
      pageValueText(reading(element), element.getAttribute("decimals")),
    ]);
  for (const element of body.querySelectorAll("tc-unit"))
    out.push([element, String(reading(element)?.unit ?? "")]);
  for (const element of body.querySelectorAll("tc-age"))
    out.push([element, pageAgeText(reading(element)?.measuredAt, data.now)]);
  return out;
}

/**
 * Fill every binding of a version 2 page for one update, and return the rules
 * (`--frac`, `--x`, `--y`, `--x0`, `--x1`, `--tc-w`, `--tc-h`, `--tc-chars`) the frame's
 * values style takes after the readings' own. `onShape` is called when this
 * update may have changed the page's layout: a state attribute set, changed
 * or removed, or a text filled or emptied (a row shown or hidden, TOM-665).
 *
 * Two options serve a scripted page's host, which fills a copy of the page
 * outside any frame (page-script.js, TOM-1038). `record(el, what)` is called
 * on every text, path `d` and fitted `viewBox` this update sets, written or
 * unchanged, with `what` "text", "d" or "viewBox". `boxOf(svg)` gives a
 * fitted drawing's size, `{ width, height }`, in place of its own box; an
 * undefined result falls back to the drawing's own box.
 */
export function fillPageBindings(
  index,
  page,
  data,
  { written, onShape, record, boxOf } = {},
) {
  const now = data.now;
  const readings = data.readings ?? {};
  const specs = page.readings ?? {};
  const series = data.series ?? {};
  const feeds = data.feeds ?? {};
  const statuses = data.statuses ?? {};
  const results = data.results ?? {};
  const freshness = data.freshness ?? null;
  const timeZone = data.timeZone;
  const locale = data.locale;
  const rules = new Map();
  const memory = written ?? new WeakMap();

  const rule = (selector, property, value) => {
    let declarations = rules.get(selector);
    if (!declarations) rules.set(selector, (declarations = new Map()));
    declarations.set(property, value === null ? "initial" : String(value));
  };
  const shape = () => onShape?.();
  const setText = (el, text) => {
    record?.(el, "text");
    const before = el.textContent;
    if (before === text) return;
    el.textContent = text;
    if (!before !== !text) shape();
  };
  const host = (el, name, value) => {
    let last = memory.get(el);
    if (!last) memory.set(el, (last = {}));
    if (value === null || value === undefined) {
      if (last[name] !== null && el.hasAttribute?.(name)) {
        el.removeAttribute(name);
        if (name !== "data-tc-key") shape();
      }
      last[name] = null;
      return;
    }
    if (last[name] !== value || el.getAttribute(name) !== value) {
      if (el.getAttribute(name) !== value && name !== "data-tc-key") shape();
      el.setAttribute(name, value);
    }
    last[name] = value;
  };
  const reading = (name) => own(readings, name);
  const spec = (name) => own(specs, name) ?? {};
  const shown = (r) => r && r.quality !== "missing";
  const storeOf = (r) =>
    shown(r) && numeric(r.storeValue) ? r.storeValue : null;
  const displayOf = (name) => {
    const r = reading(name);
    if (r?.display && numeric(r.display.a) && numeric(r.display.b))
      return r.display;
    return { a: 1, b: 0, decimals: decimalsOfText(r?.text) };
  };
  // The selector of one element alone, by the key the host sets on it: a
  // mark, time tick or day band takes its values from its window and its
  // drawing, so two with the same bindings (in nested windows, or in drawings
  // of two widths) never share a rule (TOM-665). A page cannot set the key:
  // pageBindingIndex takes away any key the page wrote.
  const selectorFor = ({ el, key }) => {
    host(el, "data-tc-key", String(key));
    return `[data-tc-key="${key}"]`;
  };

  // A window's span: a module feed series' own, else the first series read
  // over it, else the window at now; never the page's clock for a series.
  const feedSeries = (feed, field) => {
    const f = own(feeds, feed);
    return f && f.kind === "module"
      ? (own(f.series ?? {}, field) ?? null)
      : null;
  };
  const spanOf = (window) => {
    if (typeof window !== "string") return null;
    const feed = /^feed:([^.]+)\.(.+)$/.exec(window);
    if (feed !== null) {
      const s = feedSeries(feed[1], feed[2]);
      return s ? [s.fromMs, s.toMs] : null;
    }
    for (const [key, s] of Object.entries(series)) {
      if (s && key.split("|")[1] === window) return [s.fromMs, s.toMs];
    }
    return pageWindowBounds(window, now);
  };
  const readingSeries = (name, el, attrs) => {
    const w = windowOf(el)?.window;
    if (!w || w.startsWith("feed:")) return undefined;
    const points = Number(attrs["data-tc-points"] ?? 96);
    return own(series, pageSeriesKey(name, w, points)) ?? null;
  };
  // A series the host has not read yet is left out of the data (TOM-1001),
  // where one it read and found empty is null: its path keeps what it drew,
  // so a chart never says its window is empty while its history loads.
  const seriesUnread = (name, el, attrs) => {
    const w = windowOf(el)?.window;
    if (!w || w.startsWith("feed:") || data.series === undefined) return false;
    const points = Number(attrs["data-tc-points"] ?? 96);
    return !Object.hasOwn(series, pageSeriesKey(name, w, points));
  };

  // Fitted drawings: each svg's viewBox is its own CSS box, on every paint.
  const fitted = new Map();
  for (const svg of index.fits) {
    const box = boxOf?.(svg) ??
      svg.getBoundingClientRect?.() ?? { width: 0, height: 0 };
    const w = Math.round(box.width * 2) / 2;
    const h = Math.round(box.height * 2) / 2;
    const viewBox = `0 0 ${w} ${h}`;
    record?.(svg, "viewBox");
    if (svg.getAttribute("viewBox") !== viewBox)
      svg.setAttribute("viewBox", viewBox);
    fitted.set(svg, { w, h });
    rule(
      selectorOf({ "data-tc-fit": svg.getAttribute("data-tc-fit") }, [
        "data-tc-fit",
      ]),
      "--tc-w",
      w,
    );
    rule(
      selectorOf({ "data-tc-fit": svg.getAttribute("data-tc-fit") }, [
        "data-tc-fit",
      ]),
      "--tc-h",
      h,
    );
  }
  // A plot with no width or height is no plot (TOM-1000): an inset as deep
  // as its drawing gave a negative height, which flipped the value scale and
  // drew the line upside down with its area above it.
  const plotBox = (el, attrs) => {
    const svg = svgOf(el);
    if (svg === null) return null;
    const fit = fitted.get(svg);
    let box = null;
    if (fit) {
      const inset = fourNumbers(attrs["data-tc-inset"]) ?? [0, 0, 0, 0];
      const [t, r, b, l] = inset;
      box = {
        x: l,
        y: t,
        w: fit.w - l - r,
        h: fit.h - t - b,
        svg,
        fitted: true,
      };
    } else {
      const own =
        fourNumbers(attrs["data-tc-box"]) ??
        fourNumbers(svg.getAttribute("viewBox"));
      if (own !== null)
        box = {
          x: own[0],
          y: own[1],
          w: own[2],
          h: own[3],
          svg,
          fitted: false,
        };
    }
    return box !== null && box.w > 0 && box.h > 0 ? box : null;
  };

  // Every path and mark, grouped by its value scale (§4.5.3).
  const members = [];
  const groups = new Map();
  let anonymous = 0;
  for (const item of index.bound) {
    const { el, attrs, tag } = item;
    const isPath = tag === "path";
    let member = null;
    if (isPath && attrs["data-tc-path"] !== undefined) {
      const name = attrs["data-tc-path"];
      member = {
        item,
        reading: name,
        series: readingSeries(name, el, attrs) ?? null,
        unread: seriesUnread(name, el, attrs),
        shape: attrs["data-tc-shape"] ?? "line",
        mode:
          attrs["data-tc-y"] ??
          (Array.isArray(spec(name).range) ? "range" : "auto"),
      };
    } else if (isPath && attrs["data-tc-feed"] !== undefined) {
      const field = attrs["data-tc-field"];
      member = {
        item,
        feed: attrs["data-tc-feed"],
        field,
        series: feedSeries(attrs["data-tc-feed"], field),
        shape: attrs["data-tc-shape"] ?? "line",
        mode: attrs["data-tc-y"] ?? "auto",
      };
    } else if (attrs["data-tc-mark"] !== undefined) {
      const name = attrs["data-tc-mark"];
      member = {
        item,
        reading: name,
        mark: true,
        series: readingSeries(name, el, attrs) ?? null,
        shape: "line",
        mode:
          attrs["data-tc-y"] ??
          (Array.isArray(spec(name).range) ? "range" : "auto"),
      };
    }
    if (member === null) continue;
    const id = attrs["data-tc-group"] ?? `\u0000${(anonymous += 1)}`;
    member.group = id;
    members.push(member);
    let group = groups.get(id);
    if (!group) groups.set(id, (group = { members: [], scale: null }));
    group.members.push(member);
  }
  for (const group of groups.values()) {
    const first = group.members[0];
    if (first.mode === "range" && first.reading !== undefined) {
      const range = spec(first.reading).range;
      group.scale =
        Array.isArray(range) && numeric(range[0]) && numeric(range[1])
          ? [range[0], range[1]]
          : null;
    } else if (first.mode === "feed" && first.feed !== undefined) {
      const f = own(feeds, first.feed);
      group.scale = own(f?.figures?.scales ?? {}, first.field) ?? null;
    } else {
      group.scale = pageYRange(
        group.members.map((m) => ({ series: m.series, shape: m.shape })),
      );
    }
    group.reading = group.members.find((m) => m.reading !== undefined)?.reading;
    group.path = group.members.find((m) => !m.mark);
  }

  // A group's value ticks in the owner's units (§4.6): its display line, its
  // domain [lo, hi] in those units and the ticks, at its plot's height in a
  // fitted drawing.
  const groupTicks = (group) => {
    const line =
      group.reading !== undefined
        ? displayOf(group.reading)
        : { a: 1, b: 0, decimals: null };
    if (group.aligned)
      return {
        line,
        lo: group.aligned.lo,
        hi: group.aligned.hi,
        ticks: group.aligned.ticks,
      };
    let lo = line.a * group.scale[0] + line.b;
    let hi = line.a * group.scale[1] + line.b;
    if (hi < lo) [lo, hi] = [hi, lo];
    const box = group.path
      ? plotBox(group.path.item.el, group.path.item.attrs)
      : null;
    return {
      line,
      lo,
      hi,
      ticks: pageValueTicks(lo, hi, box?.fitted ? box.h : null),
    };
  };

  // Two value axes in one drawing (TOM-953): where one svg's value ticks name
  // two groups that both scale themselves (`auto`), the second group's scale
  // is set so its ticks sit on the first's, at round values of its own, and
  // its paths and marks draw on that scale. A chart's right axis then needs
  // no grid lines of its own. Any page's drawing with two tick groups is
  // aligned so, a hand-written one too; one with a single tick group is drawn
  // as before, and so is a pair the alignment cannot serve.
  const tickGroups = new Map();
  for (const { el, attrs } of index.bound) {
    const id = attrs["data-tc-scale"];
    if (id === undefined || attrs["data-tc-tick"] === undefined) continue;
    const svg = svgOf(el);
    if (svg === null) continue;
    let ids = tickGroups.get(svg);
    if (!ids) tickGroups.set(svg, (ids = []));
    if (!ids.includes(id)) ids.push(id);
  }
  const selfScaled = (group) =>
    group !== undefined &&
    group.scale !== null &&
    group.members[0].mode === "auto";
  const samePlot = (a, b) => {
    const boxA = a.path ? plotBox(a.path.item.el, a.path.item.attrs) : null;
    const boxB = b.path ? plotBox(b.path.item.el, b.path.item.attrs) : null;
    return (
      boxA !== null &&
      boxB !== null &&
      ["x", "y", "w", "h"].every((k) => Math.abs(boxA[k] - boxB[k]) < 0.5)
    );
  };
  for (const ids of tickGroups.values()) {
    if (ids.length < 2) continue;
    const first = groups.get(ids[0]);
    const second = groups.get(ids[1]);
    if (!selfScaled(first) || !selfScaled(second) || second.aligned) continue;
    // Only two axes of one plot: stacked plots in one svg (each its own
    // inset or box) keep their own ticks, spaced for their own height.
    if (!samePlot(first, second)) continue;
    const left = groupTicks(first);
    const raw = pageYRange(
      second.members.map((m) => ({ series: m.series, shape: m.shape })),
      { pad: 0 },
    );
    const line =
      second.reading !== undefined ? displayOf(second.reading) : { a: 1, b: 0 };
    if (raw === null || !(line.a > 0) || !numeric(line.b)) continue;
    const aligned = pageAlignedTicks(
      left.ticks,
      left.lo,
      left.hi,
      line.a * raw[0] + line.b,
      line.a * raw[1] + line.b,
    );
    if (aligned === null) continue;
    const scale = [
      (aligned.lo - line.b) / line.a,
      (aligned.hi - line.b) / line.a,
    ];
    if (!scale.every(numeric) || !(scale[1] > scale[0])) continue;
    second.scale = scale;
    second.aligned = aligned;
  }
  const memberOf = new Map(members.map((m) => [m.item, m]));
  // Each drawing's time tick slots (TOM-1001): its ticks are spread over the
  // slots it has, never the first ones of more than it holds.
  const timeTickSlots = new Map();
  for (const { el, attrs } of index.bound) {
    const k = Number(attrs["data-tc-time-tick"]);
    if (!Number.isInteger(k)) continue;
    const owner = svgOf(el);
    if (owner)
      timeTickSlots.set(owner, Math.max(timeTickSlots.get(owner) ?? 0, k + 1));
  }

  const missingFrac = (selector) => rule(selector, "--frac", null);
  const fracRule = (selector, value) =>
    rule(
      selector,
      "--frac",
      numeric(value) ? Math.max(0, Math.min(1, value)) : null,
    );

  for (const item of index.bound) {
    const { el, attrs, tag } = item;
    let quality;
    let zone;
    const member = memberOf.get(item);

    // A history path's `d`, a reading's or a module feed series'.
    if (member && !member.mark && tag === "path" && !member.unread) {
      const group = groups.get(member.group);
      const box = plotBox(el, attrs);
      const d =
        box === null || group.scale === null || !member.series
          ? ""
          : pageSeriesPath(member.series, {
              box,
              scale: group.scale,
              shape: member.shape,
              curve: attrs["data-tc-curve"],
            });
      record?.(el, "d");
      if (el.getAttribute("d") !== d) el.setAttribute("d", d);
      if (d === "") quality = "missing";
      // A feed's line has no reading to say it is good: once it draws it is,
      // or a line first painted before its feed arrived stayed hidden as
      // missing (TOM-1001).
      else if (member.feed !== undefined) quality = "good";
    }

    // A point of a reading's series.
    if (member?.mark) {
      const group = groups.get(member.group);
      const at = attrs["data-tc-at"] ?? "last";
      const point =
        group.scale === null
          ? null
          : pageSeriesMark(member.series, at, group.scale);
      const selector = selectorFor(item);
      rule(selector, "--x", point ? point.x : null);
      rule(selector, "--y", point ? point.y : null);
      if (point === null) quality = "missing";
    }

    // The reading's fraction on its range.
    const fracName = attrs["data-tc-frac"];
    if (fracName !== undefined) {
      const r = reading(fracName);
      const f = readingFraction(storeOf(r), spec(fracName).range);
      fracRule(selectorOf(attrs, ["data-tc-frac"]), f);
    }

    // The text a reading writes.
    const textName = attrs["data-tc-text"];
    if (textName !== undefined) {
      const r = reading(textName);
      const stat = attrs["data-tc-stat"];
      const places = attrs["data-tc-decimals"];
      if (stat !== undefined) {
        const s = readingSeries(textName, el, attrs);
        const value = pageSeriesStat(s, stat);
        const line = displayOf(textName);
        const decimals = places !== undefined ? Number(places) : line.decimals;
        if (value === null || value === undefined) setText(el, "—");
        else if (line.duration === true) {
          // A duration's statistic reads as one ("10 d", TOM-742); the series
          // is in seconds, its store unit.
          const seconds =
            stat === "change" ? value.last - value.first : Number(value);
          setText(
            el,
            stat === "change" && seconds > 0
              ? `+${formatDuration(seconds)}`
              : formatDuration(seconds),
          );
        } else if (stat === "change") {
          const delta =
            line.a * value.last + line.b - (line.a * value.first + line.b);
          setText(
            el,
            `${delta >= 0 ? "+" : "−"}${fixed(Math.abs(delta), decimals)}`,
          );
        } else setText(el, fixed(line.a * value + line.b, decimals));
      } else {
        const text =
          !shown(r) || !numeric(r.value)
            ? "—"
            : places !== undefined
              ? fixed(r.value, Number(places))
              : typeof r.text === "string" && r.text
                ? r.text
                : String(r.value);
        setText(el, text);
        // How many characters the value is, so a page can size it to the
        // room its drawing leaves (a gauge's arc) in CSS alone (TOM-665).
        rule(
          selectorOf(attrs, ["data-tc-text", "data-tc-decimals"]),
          "--tc-chars",
          [...text].length,
        );
      }
    } else if (attrs["data-tc-unit"] !== undefined) {
      setText(el, String(reading(attrs["data-tc-unit"])?.unit ?? ""));
    } else if (attrs["data-tc-age"] !== undefined) {
      const r = reading(attrs["data-tc-age"]);
      setText(
        el,
        attrs["data-tc-age-style"] === "readout"
          ? readoutAge(r, now)
          : pageAgeText(r?.measuredAt, now),
      );
    } else if (attrs["data-tc-name"] !== undefined) {
      setText(el, String(reading(attrs["data-tc-name"])?.label ?? ""));
    } else if (attrs["data-tc-equipment"] !== undefined) {
      setText(el, String(reading(attrs["data-tc-equipment"])?.equipment ?? ""));
    } else if (attrs["data-tc-device"] !== undefined) {
      setText(el, String(reading(attrs["data-tc-device"])?.device ?? ""));
    } else if (attrs["data-tc-result"] !== undefined) {
      // One of a chat answer's `calculate` results (cloud ADR 0399, TOM-870):
      // its value and unit as one text, in the owner's units, as the answer
      // gives it. The walk keeps the binding only on a chat page.
      const result = own(results, attrs["data-tc-result"]);
      const text =
        typeof result?.text === "string" && result.text !== ""
          ? result.text
          : "—";
      setText(el, text);
      rule(
        selectorOf(attrs, ["data-tc-result"]),
        "--tc-chars",
        [...text].length,
      );
      quality = text === "—" ? "missing" : "good";
    }

    // A group's value tick (§4.6).
    if (
      attrs["data-tc-scale"] !== undefined &&
      attrs["data-tc-tick"] !== undefined
    ) {
      const group = groups.get(attrs["data-tc-scale"]);
      const k = Number(attrs["data-tc-tick"]);
      const selector = selectorOf(attrs, ["data-tc-scale", "data-tc-tick"]);
      let tick = null;
      let line = { a: 1, b: 0, decimals: 0 };
      let lo = 0,
        hi = 1;
      if (group?.scale) {
        const values = groupTicks(group);
        ({ line, lo, hi } = values);
        const ticks = values.ticks;
        tick = numeric(ticks[k]) ? ticks[k] : null;
        // Ticks are round values of their own: they take the decimals their
        // values need (TOM-953 for an aligned axis, TOM-1001 for every one),
        // so a whole-knot axis reads "5", not the reading's "5.0", and a
        // whole-percent reading on a 0.5 step reads "98.5", not a second "99".
        if (ticks.length > 1)
          line = { ...line, decimals: pageTickDecimals(ticks) };
        else if (line.decimals === null) line = { ...line, decimals: 0 };
      }
      if (tick === null || hi === lo) {
        setText(el, "");
        missingFrac(selector);
        quality = "missing";
      } else {
        setText(el, fixed(tick, line.decimals));
        fracRule(selector, (tick - lo) / (hi - lo));
        quality = "good";
      }
    }

    // A time tick, a day band and the window's label (§4.6, §4.6.1).
    if (
      attrs["data-tc-time-tick"] !== undefined ||
      attrs["data-tc-day-band"] !== undefined ||
      attrs["data-tc-window-text"] !== undefined
    ) {
      const w = windowOf(el)?.window ?? null;
      const span = spanOf(w);
      const svg = svgOf(el);
      const fit = svg ? fitted.get(svg) : undefined;
      // A fitted drawing's plot width in its own pixels, else null.
      let plotW = null;
      if (fit) {
        const firstPath = members.find(
          (m) => !m.mark && svgOf(m.item.el) === svg,
        );
        const inset = fourNumbers(firstPath?.item.attrs["data-tc-inset"]) ?? [
          0, 0, 0, 0,
        ];
        plotW = fit.w - inset[1] - inset[3];
      }
      if (attrs["data-tc-time-tick"] !== undefined) {
        const selector = selectorFor(item);
        const ticks =
          span === null
            ? []
            : pageTimeTicks(
                span[0],
                span[1],
                plotW,
                timeZone,
                locale,
                (svg && timeTickSlots.get(svg)) || Infinity,
              );
        const tick = ticks[Number(attrs["data-tc-time-tick"])];
        if (tick === undefined) {
          setText(el, "");
          missingFrac(selector);
          quality = "missing";
        } else {
          setText(el, tick.label);
          fracRule(selector, tick.frac);
          quality = "good";
        }
      }
      if (attrs["data-tc-day-band"] !== undefined) {
        const selector = selectorFor(item);
        const bands =
          span === null ? [] : pageDayBands(span[0], span[1], timeZone, locale);
        const band = bands[Number(attrs["data-tc-day-band"])];
        rule(selector, "--x0", band ? band.x0 : null);
        rule(selector, "--x1", band ? band.x1 : null);
        // A day too narrow for its name goes unnamed, as HistoryChart drops
        // a label under 56 px (TOM-1001).
        if (attrs["data-tc-field"] === "label")
          setText(
            el,
            band && !(plotW !== null && (band.x1 - band.x0) * plotW < 56)
              ? band.label
              : "",
          );
        quality = band ? "good" : "missing";
      }
      if (attrs["data-tc-window-text"] !== undefined) {
        const label =
          w === null
            ? ""
            : w.startsWith("feed:")
              ? span === null
                ? ""
                : pageWindowText(
                    `${new Date(span[0]).toISOString()}/${new Date(span[1]).toISOString()}`,
                    now,
                    locale,
                    timeZone,
                  )
              : pageWindowText(w, now, locale, timeZone);
        setText(el, label);
      }
    }

    // A feed's figure (§4.8, §4.8.1).
    const feedId = attrs["data-tc-feed"];
    if (feedId !== undefined && !(tag === "path" && member)) {
      const field = attrs["data-tc-field"] ?? "";
      const slot =
        attrs["data-tc-slot"] !== undefined
          ? Number(attrs["data-tc-slot"])
          : null;
      const selector = selectorOf(attrs, [
        "data-tc-feed",
        "data-tc-field",
        "data-tc-slot",
      ]);
      const feed = own(feeds, feedId);
      const fig = feedFigure(feed, field, slot);
      // A bars feed's value or total with data-tc-decimals is written to that
      // many places from its unrounded value (§4.2, TOM-665).
      const places = attrs["data-tc-decimals"];
      let text = fig.text ?? "";
      if (places !== undefined && feed?.kind === "bars") {
        const raw =
          field === "total"
            ? feed.rawTotal
            : field === "value" && slot !== null
              ? feed.bars?.[slot]?.raw
              : undefined;
        if (numeric(raw)) text = fixed(raw, Number(places));
      }
      setText(el, text);
      host(el, "data-tone", fig.tone ?? null);
      host(el, "data-state", fig.state ?? null);
      if (fig.zone !== undefined) zone = fig.zone;
      quality = fig.quality ?? quality;
      if (fig.frac !== undefined) fracRule(selector, fig.frac);
      if (fig.point !== undefined) {
        let xy = null;
        if (fig.point) {
          const path = members.find(
            (m) => !m.mark && m.feed === feedId && m.field === fig.point.series,
          );
          const s = feedSeries(feedId, fig.point.series);
          const scale =
            (path ? groups.get(path.group).scale : null) ??
            own(feed?.figures?.scales ?? {}, fig.point.series) ??
            null;
          const from = s?.fromMs,
            to = s?.toMs;
          if (
            scale &&
            numeric(from) &&
            numeric(to) &&
            to > from &&
            scale[1] !== scale[0]
          ) {
            xy = {
              x: Math.max(
                0,
                Math.min(1, (fig.point.atMs - from) / (to - from)),
              ),
              y: Math.max(
                0,
                Math.min(
                  1,
                  (fig.point.value - scale[0]) / (scale[1] - scale[0]),
                ),
              ),
            };
          }
        }
        rule(selector, "--x", xy ? xy.x : null);
        rule(selector, "--y", xy ? xy.y : null);
        if (xy === null) quality = "missing";
      }
    }

    // A status's words and state (§4.9).
    const statusKind = attrs["data-tc-status"];
    if (statusKind === "mode" || statusKind === "anchor") {
      const status = own(statuses, statusKind) ?? null;
      const field = attrs["data-tc-field"] ?? "word";
      const selector = selectorOf(attrs, ["data-tc-status", "data-tc-field"]);
      let text = "";
      if (status !== null) {
        if (statusKind === "mode") {
          text =
            field === "caption"
              ? (status.caption ?? "")
              : field === "tag"
                ? (status.tag ?? "")
                : (status.word ?? "");
        } else if (field === "distance") text = status.distance?.value ?? "";
        else if (field === "distance-unit") text = status.distance?.unit ?? "";
        else if (field === "circle") text = status.circle?.value ?? "";
        else if (field === "circle-unit") text = status.circle?.unit ?? "";
        else if (field === "note") text = status.note ?? "";
        else if (field === "ratio") {
          fracRule(selector, status.ratio);
          text = null;
        } else text = status.word ?? "";
      } else if (field === "ratio") {
        missingFrac(selector);
        text = null;
      }
      if (text !== null) setText(el, text);
      host(el, "data-state", status?.state ?? null);
      host(
        el,
        "data-tone",
        statusKind === "anchor" ? (status?.tone ?? null) : null,
      );
      quality = status?.quality ?? "missing";
    }

    // The card's freshness line (TOM-989): the host's words for how old the
    // page's readings are, empty while they are live, and its state. It takes
    // no data-quality, so a page's rule for a missing reading never hides it.
    if (attrs["data-tc-freshness"] !== undefined) {
      setText(el, typeof freshness?.text === "string" ? freshness.text : "");
      host(
        el,
        "data-state",
        PAGE_FRESHNESS_STATES.includes(freshness?.state)
          ? freshness.state
          : null,
      );
    }

    // The reading's state (§4.4): the first reading it is bound to.
    const stateName =
      attrs["data-tc-state"] ??
      attrs["data-tc-text"] ??
      attrs["data-tc-frac"] ??
      attrs["data-tc-path"] ??
      attrs["data-tc-mark"];
    if (stateName !== undefined) {
      const r = reading(stateName);
      const store = storeOf(r);
      if (quality === undefined) quality = shown(r) ? r.quality : "missing";
      else if (
        quality !== "missing" &&
        !shown(r) &&
        attrs["data-tc-stat"] === undefined
      )
        quality = "missing";
      zone = store === null ? null : readingZone(store, spec(stateName).zones);
    }
    if (quality !== undefined) host(el, "data-quality", quality);
    if (zone !== undefined) host(el, "data-zone", zone);
  }

  let css = "";
  for (const [selector, declarations] of rules) {
    css += `html:root:root ${selector}{${[...declarations]
      .map(([property, value]) => `${property}:${value}`)
      .join(";")}}`;
  }
  return css;
}

/** One feed figure: what the host writes for one field (and slot) of one feed. */
function feedFigure(feed, field, slot) {
  const missing = { text: "", quality: "missing", frac: null };
  if (feed === null || feed === undefined) {
    // A bar draws by --frac and holds no text (§4.8), and a missing bucket's
    // label names nothing (TOM-1001): they had each drawn a stray "—".
    return {
      text: field === "bar" || field === "label" ? "" : "—",
      quality: "missing",
      frac: null,
    };
  }
  if (feed.kind === "bars") {
    const bars = feed.bars ?? [];
    if (field === "total") return { text: feed.total ?? "—", quality: "good" };
    if (field === "unit") return { text: feed.unit ?? "", quality: "good" };
    if (field === "engine") return { text: feed.engine ?? "", quality: "good" };
    const bar = slot === null ? undefined : bars[slot];
    if (bar === undefined) return missing;
    const quality =
      bar.raw === null ? "missing" : bar.partial ? "partial" : "good";
    if (field === "value") return { text: bar.value, quality };
    if (field === "label") return { text: bar.label, quality };
    if (field === "bar") {
      const max = Math.max(0, ...bars.map((b) => (numeric(b.raw) ? b.raw : 0)));
      return {
        text: "",
        quality,
        frac:
          numeric(bar.raw) && max > 0
            ? bar.raw / max
            : bar.raw === null
              ? null
              : 0,
      };
    }
    return missing;
  }
  if (feed.kind === "water") {
    const f = feed.figures ?? {};
    const rowFields = ["label", "value", "unit", "bar", "age"];
    if (slot !== null || rowFields.includes(field)) {
      const row = slot === null ? undefined : f.rows?.[slot];
      if (row === undefined) return missing;
      const base = { quality: row.quality, zone: row.severity };
      if (field === "label") return { ...base, text: row.label };
      if (field === "value") return { ...base, text: row.value };
      if (field === "unit") return { ...base, text: row.unit };
      if (field === "bar")
        return {
          ...base,
          text: "",
          frac: numeric(row.pct) ? row.pct / 100 : null,
        };
      if (field === "age") return { ...base, text: row.age ?? "" };
      return missing;
    }
    switch (field) {
      case "hero":
        return {
          text: f.hero?.value ?? "—",
          quality: f.heroQuality ?? "missing",
        };
      case "hero-unit":
        return {
          text: f.hero?.unit ?? "",
          quality: f.heroQuality ?? "missing",
        };
      case "of":
        return { text: f.hero?.of ?? "" };
      case "fullness":
        return { text: f.fullness ?? "" };
      case "attention":
        return {
          text: f.attention?.text ?? "",
          tone: f.attention?.tone ?? null,
        };
      case "levels-only":
        return { text: "", state: f.levelsOnly ? "on" : "off" };
      case "last-heard":
        return { text: f.lastHeard ?? "" };
      case "forecast":
        return { text: f.forecast ?? "" };
      case "as-of":
        return { text: f.asOf ?? "" };
      default:
        return missing;
    }
  }
  if (feed.kind === "module") {
    const figures = feed.figures ?? {};
    if (slot === null && Object.hasOwn(figures.fields ?? {}, field)) {
      return normalize(figures.fields[field]);
    }
    const row = /^([a-z]+)-/.exec(field)?.[1];
    const record =
      row === undefined || slot === null
        ? undefined
        : figures.rows?.[row]?.[slot];
    if (record === undefined || !Object.hasOwn(record, field)) return missing;
    return normalize(record[field]);
  }
  return missing;
}

function normalize(fig) {
  return {
    text: fig?.text ?? "",
    tone: fig?.tone ?? null,
    state: fig?.state,
    quality: fig?.quality ?? "good",
    zone: fig?.zone === undefined ? undefined : fig.zone,
    frac: fig?.frac,
    point: fig?.point,
  };
}
